/**
 * Page Ressources — affichée après les blocs sensibles ET en fin de parcours
 * (UI_DESIGN §5.5, RGPD_PLAN §2.1).
 *
 * Pays FR / BE / CH + Europe (112). LAVI ajoutée en V16 pour la Suisse.
 * Liens cliquables (`tel:`). Choix pays mémorisé localement (lecture seule —
 * pas un cookie, simple préférence d'affichage).
 */
import { useState } from 'react';

type Country = 'fr' | 'be' | 'ch' | 'eu';

interface Resource {
  label: string;
  detail: string;
  tel?: string;
  url?: string;
}

const RESOURCES: Record<Country, Resource[]> = {
  fr: [
    {
      label: '3919',
      detail: 'Violences faites aux femmes — écoute, information, orientation.',
      tel: '3919',
    },
    {
      label: '3114',
      detail: 'Numéro national de prévention du suicide, 24h/24, 7j/7.',
      tel: '3114',
    },
    {
      label: '119',
      detail: 'Enfance en danger.',
      tel: '119',
    },
  ],
  be: [
    {
      label: '0800 30 030',
      detail: 'Écoute violences conjugales (Communauté française de Belgique).',
      tel: '080030030',
    },
    {
      label: '107',
      detail: 'Télé-Accueil — écoute anonyme 24h/24.',
      tel: '107',
    },
  ],
  ch: [
    {
      label: '143',
      detail: 'La Main Tendue — écoute anonyme 24h/24.',
      tel: '143',
    },
    {
      label: 'LAVI — lavi.ch',
      detail: 'Aide aux victimes (cantons suisses). Centres de consultation gratuits.',
      url: 'https://www.lavi.ch',
    },
    {
      label: '117',
      detail: 'Police (urgences en Suisse).',
      tel: '117',
    },
  ],
  eu: [
    {
      label: '112',
      detail: "Numéro d'urgence européen — police, pompiers, ambulance.",
      tel: '112',
    },
  ],
};

const COUNTRY_LABELS: Record<Country, string> = {
  fr: 'France',
  be: 'Belgique',
  ch: 'Suisse',
  eu: 'Europe',
};

const COUNTRY_KEY = 'wfs_country_pref';

function readCountry(): Country {
  try {
    const v = window.localStorage.getItem(COUNTRY_KEY);
    if (v === 'fr' || v === 'be' || v === 'ch' || v === 'eu') return v;
  } catch {
    /* */
  }
  return 'fr';
}

function writeCountry(c: Country): void {
  try {
    window.localStorage.setItem(COUNTRY_KEY, c);
  } catch {
    /* */
  }
}

interface HelplinePageProps {
  /** Variante d'affichage. `inline` = encart léger après un bloc ; `full` = page de fin. */
  variant?: 'inline' | 'full';
  /** Titre personnalisé. */
  title?: string;
}

export function HelplinePage({ variant = 'inline', title }: HelplinePageProps) {
  const [country, setCountry] = useState<Country>(readCountry());
  const headingText = title ?? 'Ressources d\'aide';
  const resources = RESOURCES[country];

  const handleCountry = (c: Country) => {
    setCountry(c);
    writeCountry(c);
  };

  return (
    <section
      className={variant === 'full' ? 'card max-w-prose mx-auto' : 'info-callout max-w-prose'}
      aria-labelledby={`helpline-${variant}`}
    >
      <h2 id={`helpline-${variant}`} className="font-semibold mb-2">
        {headingText}
      </h2>
      <p className="text-text-muted text-sm mb-4">
        Si vous en ressentez le besoin, voici des personnes à qui parler. Cela
        n'interrompt pas le questionnaire.
      </p>

      <fieldset className="mb-4">
        <legend className="text-sm text-text-muted mb-2">Pays :</legend>
        <div className="flex flex-wrap gap-2">
          {(Object.keys(COUNTRY_LABELS) as Country[]).map((c) => (
            <label
              key={c}
              className={`text-sm px-3 py-2 rounded-lg border cursor-pointer ${
                country === c
                  ? 'border-accent bg-selected-bg text-text'
                  : 'border-surface-2 bg-surface hover:bg-surface-2'
              }`}
            >
              <input
                type="radio"
                name={`country-${variant}`}
                value={c}
                checked={country === c}
                onChange={() => handleCountry(c)}
                className="sr-only"
              />
              {COUNTRY_LABELS[c]}
            </label>
          ))}
        </div>
      </fieldset>

      <ul className="space-y-3">
        {resources.map((r) => (
          <li key={r.label} className="flex flex-col sm:flex-row sm:items-baseline gap-2">
            <span className="font-medium text-text">
              {r.tel ? (
                <a
                  href={`tel:${r.tel}`}
                  className="text-accent underline underline-offset-2 hover:text-accent-hover"
                >
                  {r.label}
                </a>
              ) : r.url ? (
                <a
                  href={r.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent underline underline-offset-2 hover:text-accent-hover"
                >
                  {r.label}
                </a>
              ) : (
                r.label
              )}
            </span>
            <span className="text-sm text-text-muted">{r.detail}</span>
          </li>
        ))}
      </ul>

      {country !== 'eu' && (
        <p className="text-xs text-text-muted mt-4">
          En Europe, le numéro d'urgence universel reste le{' '}
          <a href="tel:112" className="text-accent underline">
            112
          </a>
          .
        </p>
      )}
    </section>
  );
}
